/**
 * Xiangqi FEN, following the de-facto standard documented at
 * xqbase.com/protocol/cchess_fen.htm.
 *
 * Six space-separated fields:
 *   1. Piece placement, rank 9 (Black's back rank) first down to rank 0,
 *      files a→i within each rank. Digits are runs of empty points.
 *   2. Side to move: `w` = Red, `b` = Black. (Some tools use `r` for Red; we
 *      accept anything that is not `b` as Red on input, per common practice.)
 *   3. Castling — always `-` (no such move in xiangqi).
 *   4. En passant — always `-`.
 *   5. Halfmove clock (plies since last capture).
 *   6. Fullmove number.
 *
 * Piece letters: K/k king, A/a advisor, B/b elephant, N/n horse, R/r rook,
 * C/c cannon, P/p pawn. Uppercase = Red, lowercase = Black.
 */

import { Position } from "./position.js";
import { Color, FILES, PieceType, type Piece } from "./types.js";

export const START_FEN =
  "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C5C1/9/RNBAKABNR w - - 0 1";

const LETTER_TO_TYPE: Record<string, PieceType> = {
  k: PieceType.King,
  a: PieceType.Advisor,
  b: PieceType.Elephant,
  n: PieceType.Horse,
  r: PieceType.Rook,
  c: PieceType.Cannon,
  p: PieceType.Pawn,
};

const TYPE_TO_LETTER = ["k", "a", "b", "n", "r", "c", "p"] as const;

export function parseFen(fen: string): Position {
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 1) throw new Error(`Invalid FEN: empty`);
  const placement = parts[0]!;
  const ranks = placement.split("/");
  if (ranks.length !== 10) {
    throw new Error(`Invalid FEN: expected 10 ranks, got ${ranks.length} in "${placement}"`);
  }

  const pos = new Position();
  // FEN lists rank 9 first, so ranks[0] -> rank 9, ranks[9] -> rank 0.
  for (let i = 0; i < 10; i++) {
    const rank = 9 - i;
    const rowStr = ranks[i]!;
    let file = 0;
    for (const ch of rowStr) {
      if (ch >= "1" && ch <= "9") {
        file += ch.charCodeAt(0) - "0".charCodeAt(0);
      } else {
        const lower = ch.toLowerCase();
        const type = LETTER_TO_TYPE[lower];
        if (type === undefined) throw new Error(`Invalid FEN: unknown piece '${ch}'`);
        if (file >= FILES) throw new Error(`Invalid FEN: rank ${rank} overflows`);
        const color = ch === lower ? Color.Black : Color.Red;
        pos.board[rank * FILES + file] = { color, type };
        file += 1;
      }
    }
    if (file !== FILES) {
      throw new Error(`Invalid FEN: rank ${rank} has ${file} points, expected ${FILES}`);
    }
  }

  const side = parts[1] ?? "w";
  pos.sideToMove = side === "b" ? Color.Black : Color.Red;
  pos.halfmoveClock = parts[4] !== undefined ? parseInt(parts[4], 10) || 0 : 0;
  pos.fullmoveNumber = parts[5] !== undefined ? parseInt(parts[5], 10) || 1 : 1;
  return pos;
}

export function toFen(pos: Position): string {
  const rows: string[] = [];
  for (let rank = 9; rank >= 0; rank--) {
    let row = "";
    let empty = 0;
    for (let file = 0; file < FILES; file++) {
      const p: Piece | null = pos.board[rank * FILES + file] ?? null;
      if (!p) {
        empty += 1;
        continue;
      }
      if (empty > 0) {
        row += String(empty);
        empty = 0;
      }
      const letter = TYPE_TO_LETTER[p.type];
      row += p.color === Color.Red ? letter.toUpperCase() : letter;
    }
    if (empty > 0) row += String(empty);
    rows.push(row);
  }
  const side = pos.sideToMove === Color.Red ? "w" : "b";
  return `${rows.join("/")} ${side} - - ${pos.halfmoveClock} ${pos.fullmoveNumber}`;
}
